import { useMemo, useState } from "react";
import {
    Clock,
    MapPin,
    Video,
    Eye,
    CalendarCheck2,
    CalendarClock,
    CalendarDays,
    CheckCircle2,
    Phone
} from "lucide-react";
import JoinMeetingModal from "./JoinMeetingModal.jsx";
import AddRemarksModal from "./AddRemarksModal.jsx";
import { formatDate } from "./bookingUtils.jsx";

function startOfDay(d) {
    const copy = new Date(d);
    copy.setHours(0, 0, 0, 0);
    return copy;
}

function isSameDay(a, b) {
    return (
        a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate()
    );
}

export default function Bookings({ bookings, onCancel, onAddRemarks, showJoinMeeting = false }) {
    const [joinTarget, setJoinTarget] = useState(null);
    const [remarksTarget, setRemarksTarget] = useState(null);

    const { total, upcoming, today, completed } = useMemo(() => {
        const now = new Date();
        const todayStart = startOfDay(now);

        let upcomingCount = 0;
        let todayCount = 0;
        let completedCount = 0;

        bookings.forEach((b) => {
            const d = new Date(b.date);
            if (Number.isNaN(d.getTime())) return;

            if (isSameDay(d, now)) todayCount += 1;
            if (startOfDay(d) >= todayStart) {
                upcomingCount += 1;
            } else {
                completedCount += 1;
            }
        });

        return {
            total: bookings.length,
            upcoming: upcomingCount,
            today: todayCount,
            completed: completedCount,
        };
    }, [bookings]);

    const stats = [
        { title: "Total Bookings", value: total, color: "bg-indigo-100 text-indigo-600", icon: CalendarDays },
        { title: "Upcoming", value: upcoming, color: "bg-cyan-100 text-cyan-600", icon: CalendarClock },
        { title: "Today", value: today, color: "bg-green-100 text-green-600", icon: CalendarCheck2 },
        { title: "Completed", value: completed, color: "bg-slate-100 text-slate-600", icon: CheckCircle2 },
    ];

    const handleSaveRemarks = (bookingId, remarks) => {
        onAddRemarks?.(bookingId, remarks);
    };

    const handleCancelBooking = (bookingId) => {
        onCancel?.(bookingId);
    };

    const colSpan = showJoinMeeting ? 7 : 6;

    return (
        <div className="space-y-6">
            {/* Stat cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                {stats.map((item, i) => {
                    const Icon = item.icon;
                    return (
                        <div
                            key={i}
                            className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm hover:shadow-md transition"
                        >
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-slate-500">{item.title}</p>
                                    <h2 className="text-3xl font-bold mt-2 text-slate-900">{item.value}</h2>
                                </div>
                                <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${item.color}`}>
                                    <Icon size={22} />
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Bookings table */}
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-slate-100 bg-slate-50/80 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                                <th className="px-4 py-3">Name</th>
                                <th className="px-4 py-3">Date & Time</th>
                                <th className="px-4 py-3">Location</th>
                                <th className="px-4 py-3">Email</th>
                                <th className="px-4 py-3">Mobile</th>
                                {showJoinMeeting && <th className="px-4 py-3">Meeting</th>}
                                <th className="px-4 py-3 text-right">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {bookings.map((b) => (
                                <tr key={b.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60 transition-colors">
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-3">
                                            <div className="h-9 w-9 shrink-0 rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center text-xs font-semibold text-white">
                                                {b.initials}
                                            </div>
                                            <div>
                                                <p className="font-medium text-slate-900">{b.mentorName}</p>
                                                <p className="text-xs text-slate-400">{b.course}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-slate-600">
                                        <p>{formatDate(b.date)}</p>
                                        <p className="flex items-center gap-1 text-xs text-slate-400">
                                            <Clock className="h-3 w-3" /> {b.time}
                                        </p>
                                    </td>
                                    <td className="px-4 py-3 text-slate-600">
                                        <span className="flex items-center gap-1.5">
                                            <MapPin className="h-3.5 w-3.5 text-slate-400" />
                                            {b.location}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-slate-600">{b.mailid}</td>
                                    <td className="px-4 py-3 text-slate-600">
                                        <span className="flex items-center gap-1.5">
                                            <Phone className="h-3.5 w-3.5 text-slate-400" />
                                            {b.mobile}
                                        </span>
                                    </td>

                                    {showJoinMeeting && (
                                        <td className="px-4 py-3">
                                            <button
                                                onClick={() => setJoinTarget(b)}
                                                className="inline-flex items-center gap-1.5 rounded-lg bg-green-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-green-700 transition-colors"
                                            >
                                                <Video className="h-3.5 w-3.5" />
                                                Join Meeting
                                            </button>
                                        </td>
                                    )}

                                    <td className="px-4 py-3 text-right">
                                        <button
                                            onClick={() => setRemarksTarget(b)}
                                            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 transition-colors"
                                        >
                                            <Eye className="h-3.5 w-3.5" />
                                            View
                                        </button>
                                    </td>
                                </tr>
                            ))}

                            {bookings.length === 0 && (
                                <tr>
                                    <td colSpan={colSpan} className="px-4 py-10 text-center text-sm text-slate-400">
                                        No upcoming bookings match your search.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <JoinMeetingModal booking={joinTarget} onClose={() => setJoinTarget(null)} />
                <AddRemarksModal
                    booking={remarksTarget}
                    onClose={() => setRemarksTarget(null)}
                    onSave={handleSaveRemarks}
                    onCancelBooking={handleCancelBooking}
                />
            </div>
        </div>
    );
}